import {
  Box,
  Button,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import {
  useMainFrameDispatch,
  useMainFrameState,
} from '../../../../component/MainUIFrame/MainFrame';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import {
  ACTION_CLOSE_RIGHT_FLOATING_DRAWER,
  ACTION_OPEN_RIGHT_FLOATING_DRAWER,
} from '../../../../component/MainUIFrame/constants';
import { LoadingButton } from '@mui/lab';
import { ehrAPIBaseService, ehrAPIService } from '../../../../http-common';
import { generateNotifications } from '../../../../component/reusableLogic';
import { useSnackbar } from 'notistack';
import { getTenantFromToken } from '../../../../config/common';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const ExportAllPatient = props => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      format: 'json',
      archivePassword: '',
    },
  });
  const { isRightDrawerOpen } = useMainFrameState();
  const dispatch = useMainFrameDispatch();

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmationPassword, setShowConfirmationPassword] =
    useState(false);

  const reduxDispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const FormError = ({ errors }) => {
    return (
      <Typography variant="caption" color="error">
        {errors}
      </Typography>
    );
  };

  const onSubmit = async data => {
    try {
      setLoading(true);
      let params = {
        name: data?.name || '',
        archivePassword: data?.archivePassword,
      };
      let response = await ehrAPIBaseService.post(
        `/export/tenants/${getTenantFromToken()}/all-patient-export`,
        params
      );
      if ([200, 202].includes(response?.status)) {
        generateNotifications({
          title: 'Success!',
          descriptions:
            'All Patient export request has been processed successfully, it will be available for download shortly.',
          enqueueSnackbar,
          closeSnackbar,
          reduxDispatch,
        });
        props?.gridRefreshCallback();
        props?.setRenderGrid(props?.renderGrid + 1);
        dispatch({ type: ACTION_CLOSE_RIGHT_FLOATING_DRAWER });
      } else {
        generateNotifications({
          title: 'Opps!',
          descriptions:
            response?.data?.message ||
            'Something went wrong, please try again later.',
          enqueueSnackbar,
          closeSnackbar,
          reduxDispatch,
        });
      }
    } catch (e) {
      generateNotifications({
        title: 'Opps!',
        descriptions:
          e?.message || 'Something went wrong, please try again later.',
        enqueueSnackbar,
        closeSnackbar,
        reduxDispatch,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 60px)',
        }}
      >
        <Grid sx={{ flex: '1', overflow: 'auto', paddingBottom: '10px' }}>
          <Grid
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
            container
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: isRightDrawerOpen ? 'flex-start' : 'center',
                alignItems: 'center',
              }}
            >
              {isRightDrawerOpen ? (
                <IconButton
                  onClick={() => {
                    dispatch({ type: ACTION_CLOSE_RIGHT_FLOATING_DRAWER });
                  }}
                >
                  <Icon>chevron_right</Icon>
                </IconButton>
              ) : (
                <IconButton
                  onClick={() => {
                    dispatch({ type: ACTION_OPEN_RIGHT_FLOATING_DRAWER });
                  }}
                >
                  <Icon>chevron_left</Icon>
                </IconButton>
              )}
            </Box>
            <Typography color="primary" px={2} fontSize={20}>
              Export All Patient
            </Typography>
          </Grid>
          <Grid container px={2} spacing={1} mt={1}>
            <Grid item sm={12}>
              <Typography sx={{ fontSize: 14 }}>
                Job Name <font color="red">*</font>
              </Typography>
              <Controller
                name={'name'}
                control={control}
                rules={{
                  required: { value: true, message: 'Job Name is required' },
                  maxLength: {
                    value: 50,
                    message: 'Job name not contain more than 50 characters',
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      type="text"
                      onChange={e => onChange(e.target.value)}
                      value={value}
                      error={Boolean(errors.name)}
                    />
                  </>
                )}
              />
              {errors?.name && <FormError errors={errors?.name?.message} />}
            </Grid>
            <Grid item sm={12}>
              <Typography sx={{ fontSize: 14 }}>
                Format <font color="red">*</font>
              </Typography>
              <Controller
                name="format"
                control={control}
                rules={{
                  required: 'Format is required',
                }}
                render={({ field }) => (
                  <>
                    <Select
                      fullWidth
                      name="format"
                      key={field?.value}
                      value={field?.value}
                      onChange={e => field?.onChange(e.target.value)}
                      size="small"
                      error={errors.format}
                    >
                      {/* <MenuItem value="ccda">CCDA</MenuItem> */}
                      {/* <MenuItem value="xml">XML</MenuItem> */}
                      <MenuItem value="json">JSON</MenuItem>
                      {/* <MenuItem value="fhir">FHIR</MenuItem> */}
                    </Select>
                    <FormError
                      errors={errors.format && errors.format?.message}
                    />
                  </>
                )}
              />
            </Grid>
            <Grid item sm={12}>
              <Typography sx={{ fontSize: 14 }}>
                Password <font color="red">*</font>
              </Typography>
              <Controller
                name={'archivePassword'}
                control={control}
                rules={{
                  required: { value: true, message: 'Password is required' },
                  maxLength: {
                    value: 16,
                    message:
                      'Password should not contain more than 16 characters',
                  },
                  minLength: {
                    value: 8,
                    message:
                      'Password should not contain less than 8 characters',
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      onChange={e => onChange(e.target.value)}
                      value={value}
                      error={Boolean(errors.archivePassword)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}
              />
              {errors?.archivePassword && (
                <FormError errors={errors?.archivePassword?.message} />
              )}
            </Grid>
            <Grid item sm={12}>
              <Typography sx={{ fontSize: 14 }}>
                Confirm Password <font color="red">*</font>
              </Typography>
              <Controller
                name={'confirmPassword'}
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Confirmation Password is required',
                  },
                  maxLength: {
                    value: 16,
                    message:
                      'Password should not contain more than 16 characters',
                  },
                  minLength: {
                    value: 8,
                    message:
                      'Password should not contain less than 8 characters',
                  },
                  validate: val => {
                    if (watch('archivePassword') != val) {
                      return 'Your passwords do not match';
                    }
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      type={showConfirmationPassword ? 'text' : 'password'}
                      onChange={e => onChange(e.target.value)}
                      value={value}
                      error={Boolean(errors.confirmPassword)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmationPassword(
                                  !showConfirmationPassword
                                )
                              }
                              edge="end"
                            >
                              {showConfirmationPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}
              />
              {errors?.confirmPassword && (
                <FormError errors={errors?.confirmPassword?.message} />
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid sx={{ marginBottom: '-50px' }} py={1}>
          <Grid container px={2} spacing={1}>
            <Grid item sm={12}>
              <LoadingButton
                name="updateAnnotations"
                onClick={handleSubmit(onSubmit)}
                variant="contained"
                fullWidth
                loading={loading}
              >
                Export
              </LoadingButton>
            </Grid>
            <Grid item sm={12}>
              <Button
                onClick={() => {
                  dispatch({ type: ACTION_CLOSE_RIGHT_FLOATING_DRAWER });
                }}
                variant="outlined"
                fullWidth
              >
                Close
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default ExportAllPatient;
